// SPDX-License-Identifier: AGPL-3.0-or-later

import {PREMIUM_PRODUCT_NAME} from '@app/features/app/config/I18nDisplayConstants';
import Authentication from '@app/features/auth/state/Authentication';
import * as ModalCommands from '@app/features/ui/commands/ModalCommands';
import * as UserCommands from '@app/features/user/commands/UserCommands';
import {AvatarDecorationOverlay} from '@app/features/user/components/AvatarDecorationOverlay';
import styles from '@app/features/user/components/modals/tabs/my_profile_tab/AvatarDecorationPicker.module.css';
import {AvatarDecorationPickerModal} from '@app/features/user/components/modals/tabs/my_profile_tab/AvatarDecorationPickerModal';
import AvatarDecorationCatalog from '@app/features/user/state/AvatarDecorationCatalog';
import Users from '@app/features/user/state/Users';
import {
	getAvatarDecorationDisplayDescription,
	getAvatarDecorationDisplayName,
} from '@app/features/user/utils/AvatarDecorationCopy';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {CaretRightIcon} from '@phosphor-icons/react';
import {observer} from 'mobx-react-lite';
import {useEffect} from 'react';

const TITLE_DESCRIPTOR = msg({
	message: 'Avatar decoration',
	comment: 'Section title in profile editing for the avatar decoration picker.',
});
const DESCRIPTION_DESCRIPTOR = msg({
	message: 'Shown around your avatar on profiles. Requires {premiumProductName}.',
	comment:
		'Helper text under the avatar decoration picker in profile editing. Preserve {premiumProductName}; it is inserted by code.',
});
const NONE_DESCRIPTOR = msg({
	message: 'None',
	comment: 'Shown when no avatar decoration is equipped.',
});
const CHANGE_DESCRIPTOR = msg({
	message: 'Change avatar decoration',
	comment: 'Opens the avatar decoration picker modal when a decoration is already equipped.',
});
const CHOOSE_DESCRIPTOR = msg({
	message: 'Choose avatar decoration',
	comment: 'Opens the avatar decoration picker modal when no decoration is equipped.',
});

interface AvatarDecorationPickerProps {
	value: string | null;
	disabled?: boolean;
	onChange: (decorationId: string | null) => void;
}

export const AvatarDecorationPicker = observer(function AvatarDecorationPicker({
	value,
	disabled = false,
	onChange,
}: AvatarDecorationPickerProps) {
	const {i18n} = useLingui();
	useEffect(() => {
		void AvatarDecorationCatalog.load();
	}, []);
	const selected = AvatarDecorationCatalog.getById(value);
	const selectedName = selected ? getAvatarDecorationDisplayName(selected, i18n.locale) : null;
	const selectedDescription = selected
		? getAvatarDecorationDisplayDescription(selected, i18n.locale)
		: undefined;
	const applyDecoration = (decorationId: string | null) => {
		onChange(decorationId);
		const currentUserId = Authentication.currentUserId ?? Users.currentUserId;
		if (!currentUserId) {
			return;
		}
		Users.setAvatarDecorationId(currentUserId, decorationId);
		void UserCommands.update({avatar_decoration_id: decorationId}).catch(() => undefined);
	};
	const openPicker = () => {
		if (disabled) {
			return;
		}
		ModalCommands.push(ModalCommands.modal(() => <AvatarDecorationPickerModal value={value} onChange={applyDecoration} />));
	};
	return (
		<div data-flx="user.my-profile-tab.avatar-decoration-picker">
			<div className={styles.header}>
				<div className={styles.title}>{i18n._(TITLE_DESCRIPTOR)}</div>
				<div className={styles.description}>
					{i18n._(DESCRIPTION_DESCRIPTOR, {premiumProductName: PREMIUM_PRODUCT_NAME})}
				</div>
			</div>
			<button
				type="button"
				className={styles.trigger}
				disabled={disabled}
				onClick={openPicker}
				data-flx="user.my-profile-tab.avatar-decoration-picker.trigger"
			>
				<div className={styles.triggerPreview}>
					<div className={styles.avatarStandIn} />
					{selected ? <AvatarDecorationOverlay userId="" decorationId={selected.id} /> : null}
				</div>
				<div className={styles.triggerCopy}>
					<div className={styles.triggerName}>{selectedName ?? i18n._(NONE_DESCRIPTOR)}</div>
					{selectedDescription ? <div className={styles.triggerCaption}>{selectedDescription}</div> : null}
				</div>
				<span className={styles.triggerAction}>
					{i18n._(selected ? CHANGE_DESCRIPTOR : CHOOSE_DESCRIPTOR)}
					<CaretRightIcon size={16} weight="bold" />
				</span>
			</button>
		</div>
	);
});
